import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import DataTable from 'react-data-table-component';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { fetchDistributorTransactionStats } from '../../../../services/Http/DistributorApi';
import { useCurrency } from '../../../../context/CurrencyProvider';

const DistributorTransactionsOverview = ({ period }) => {
    const { t } = useTranslation();
    const { formatResolvedAmount } = useCurrency();
    const [records, setRecords] = useState([]);
    const [totals, setTotals] = useState(null);
    const [meta, setMeta] = useState({ total: 0, per_page: 25, current_page: 1 });
    const [loading, setLoading] = useState(false);
    const [sort, setSort] = useState({ sortBy: 'date', sortDirection: 'desc' });

    const loadStats = useCallback(async ({ periodKey, page, perPage, sortOptions }) => {
        setLoading(true);
        try {
            const response = await fetchDistributorTransactionStats({
                period: periodKey,
                page,
                perPage,
                sortBy: sortOptions.sortBy,
                sortDirection: sortOptions.sortDirection,
            });
            setRecords(response.stats.data || []);
            setMeta({
                total: response.stats.total,
                per_page: response.stats.per_page,
                current_page: response.stats.current_page,
            });
            setTotals(response.totals);
        } catch (error) {
            toast.error(t('DistributorTransactions.errors.loadStats'));
        } finally {
            setLoading(false);
        }
    }, [t]);

    const perPageRef = useRef(meta.per_page);
    const sortRef = useRef(sort);

    useEffect(() => {
        perPageRef.current = meta.per_page;
    }, [meta.per_page]);

    useEffect(() => {
        sortRef.current = sort;
    }, [sort]);

    useEffect(() => {
        loadStats({ periodKey: period, page: 1, perPage: perPageRef.current, sortOptions: sortRef.current });
    }, [period, loadStats]);

    const columns = useMemo(() => ([
        {
            name: t('DistributorTransactions.table.columns.date'),
            selector: (row) => new Date(row.date).toLocaleDateString(),
            sortable: true,
        },
        {
            name: t('DistributorTransactions.table.columns.manufacturer'),
            selector: (row) => row.manufacturer_name || t('common.unknown'),
            sortable: true,
        },
        {
            name: t('DistributorTransactions.table.columns.retailer'),
            selector: (row) => row.retailer_name || t('common.unknown'),
            sortable: true,
        },
        {
            name: t('DistributorTransactions.table.columns.venue'),
            selector: (row) => row.venue_name || t('common.unknown'),
            sortable: true,
        },
        {
            name: t('DistributorTransactions.table.columns.inboundValue'),
            selector: (row) => formatResolvedAmount({
                eurAmount: row.sell_in_value,
                localAmount: row.sell_in_value_local ?? row.sell_in_local,
                localCurrencyId: row.currency_id ?? row.sell_in_currency_id,
                localCurrencyCode: row.currency_code ?? row.sell_in_currency_code,
            }),
            sortable: true,
        },
        {
            name: t('DistributorTransactions.table.columns.outboundValue'),
            selector: (row) => formatResolvedAmount({
                eurAmount: row.sell_through_value,
                localAmount: row.sell_through_value_local ?? row.outbound_value_local,
                localCurrencyId: row.currency_id ?? row.sell_through_currency_id,
                localCurrencyCode: row.currency_code ?? row.sell_through_currency_code,
            }),
            sortable: true,
        },
        {
            name: t('DistributorTransactions.table.columns.transactions'),
            selector: (row) => row.transactions_count,
            sortable: true,
        },
    ]), [t, formatResolvedAmount]);

    return (
        <div>
            <Cards>
                <StatCard>
                    <label>{t('DistributorTransactions.cards.inbound')}</label>
                    <strong>{formatResolvedAmount({
                        eurAmount: totals?.sell_in_value,
                        localAmount: totals?.sell_in_value_local ?? totals?.sell_in_local,
                        localCurrencyId: totals?.currency_id ?? totals?.sell_in_currency_id,
                        localCurrencyCode: totals?.currency_code ?? totals?.sell_in_currency_code,
                    })}</strong>
                    <small>{t('DistributorTransactions.cards.units', { count: totals?.sell_in_quantity || 0 })}</small>
                </StatCard>
                <StatCard>
                    <label>{t('DistributorTransactions.cards.outbound')}</label>
                    <strong>{formatResolvedAmount({
                        eurAmount: totals?.sell_through_value,
                        localAmount: totals?.sell_through_value_local ?? totals?.outbound_value_local,
                        localCurrencyId: totals?.currency_id ?? totals?.sell_through_currency_id,
                        localCurrencyCode: totals?.currency_code ?? totals?.sell_through_currency_code,
                    })}</strong>
                    <small>{t('DistributorTransactions.cards.units', { count: totals?.sell_through_quantity || 0 })}</small>
                </StatCard>
                <StatCard>
                    <label>{t('DistributorTransactions.cards.manufacturers')}</label>
                    <strong>{totals?.distinct_manufacturers || 0}</strong>
                </StatCard>
                <StatCard>
                    <label>{t('DistributorTransactions.cards.retailers')}</label>
                    <strong>{totals?.distinct_retailers || 0}</strong>
                </StatCard>
                <StatCard>
                    <label>{t('DistributorTransactions.cards.venues')}</label>
                    <strong>{totals?.distinct_venues || 0}</strong>
                </StatCard>
                <StatCard>
                    <label>{t('DistributorTransactions.cards.transactions')}</label>
                    <strong>{totals?.transactions_count || 0}</strong>
                </StatCard>
            </Cards>

            <DataTable
                columns={columns}
                data={records}
                progressPending={loading}
                pagination
                paginationServer
                paginationTotalRows={meta.total}
                paginationPerPage={meta.per_page}
                onChangePage={(page) => loadStats({ periodKey: period, page, perPage: meta.per_page, sortOptions: sort })}
                onChangeRowsPerPage={(perPage, page) => loadStats({ periodKey: period, page, perPage, sortOptions: sort })}
                onSort={(column, direction) => {
                    const sortBy = column.selector === columns[0].selector
                        ? 'date'
                        : column.selector === columns[4].selector
                            ? 'sell_in_value'
                            : column.selector === columns[5].selector
                                ? 'sell_through_value'
                                : 'transactions_count';
                    const next = { sortBy, sortDirection: direction };
                    setSort(next);
                    loadStats({ periodKey: period, page: meta.current_page, perPage: meta.per_page, sortOptions: next });
                }}
                sortServer
                highlightOnHover
            />
        </div>
    );
};

export default DistributorTransactionsOverview;

const Cards = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
    gap: 1rem;
    margin-bottom: 1.5rem;
`;

const StatCard = styled.div`
    border: 1px solid rgba(130, 22, 146, 0.15);
    border-radius: 12px;
    padding: 1rem;
    text-align: center;
    background: rgba(130, 22, 146, 0.02);

    label {
        display: block;
        color: ${({ theme }) => theme.colors.textSecondary || '#666'};
        margin-bottom: 0.375rem;
    }

    strong {
        display: block;
        font-size: 1.4rem;
        color: rgba(130, 22, 146, 1);
    }

    small {
        color: ${({ theme }) => theme.colors.textSecondary || '#666'};
    }
`;
